import type { ReactNode } from "react"
import { Box, Skeleton, Stack, Typography } from "@mui/material"

import { formatQuantity } from "helpers/utils/common"
import EntityLink from "widgets/common/EntityLink"
import GroupChips from "widgets/deviceGroups/GroupChips"
import type { EntityPopupField } from "./helpers"

export const Value = ({ children }: { children: ReactNode }) => (
  <Typography fontWeight={500} sx={{ fontVariantNumeric: "tabular-nums" }}>
    {children}
  </Typography>
)

interface FieldValueProps {
  field: EntityPopupField
  locale: string
}

const FieldValue = ({ field, locale }: FieldValueProps) => {
  switch (field.type) {
    case "text":
      return (
        <>
          {[field.value].flat().map((line, i) => (
            <Value key={`${i}-${line}`}>{line}</Value>
          ))}
        </>
      )
    case "number":
      return <Value>{formatQuantity(field.value, field.unit ?? "", locale)}</Value>
    case "entity-link":
      if (field.loading) return <Skeleton width={80} />
      return (
        <Stack alignItems="flex-end">
          {field.value.map((ref) => (
            // A flex anchor, so the name lines up with the label instead of
            // riding on the anchor's own text baseline.
            <Box
              key={`${ref.entity}-${ref.id}`}
              sx={{ display: "flex", "& > a": { display: "flex", minWidth: 0 } }}
            >
              <EntityLink entity={ref.entity} id={ref.id} name={ref.name} />
            </Box>
          ))}
        </Stack>
      )
    case "chips":
      return (
        <Box display="flex" justifyContent="flex-end">
          <GroupChips groups={field.value} />
        </Box>
      )
  }
}

export default FieldValue
